import { useCallback, useContext, useState } from 'react';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';

import AccountPickerContext from 'contexts/AccountPickerContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import DashboardsContext from 'contexts/DashboardsContext';
import ToastContext from 'contexts/ToastContext';
import { PortalsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import createDashboardGadget from 'api/createDashboardGadget';
import withoutNulls from 'api/expression/withoutNulls';
import { buildDashboardShow } from 'navigation/appRoutes';
import getIdentifier from 'getIdentifier';
import STORE from 'store';
import {
  buildDashboardGadgetFromSettings,
  resolveReportGadgetSettings,
} from 'utils/copyGadgetSettings';

const useConvertReportToDashboard = () => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { showToast } = useContext(ToastContext);
  const { selectedPortal } = useContext(PortalsContext);
  const { addOrUpdateDashboard } = useContext(DashboardsContext);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const convertReportToDashboard = useCallback(
    async ({
      report,
      dashboardName,
      access,
      keepOriginal,
      onSuccess,
    }: {
      report: ReportType;
      dashboardName: string;
      access: ResourceAccess;
      keepOriginal: boolean;
      onSuccess?: () => void;
    }) => {
      if (!report) {
        return;
      }

      if (!report.canvas.cards || report.canvas.cards.length === 0) {
        showToast('Cannot convert empty report to dashboard');
        return;
      }

      setIsLoading(true);

      try {
        const now = DateTime.utc().toISO();

        // Extract unique report gadget IDs from canvas
        const reportGadgetIds = report.canvas.cards
          .filter((card) => card.content.type === 'Report Gadget')
          .map((card) =>
            card.content.type === 'Report Gadget'
              ? card.content.reportGadgetId
              : '',
          )
          .filter((id, index, self) => id && self.indexOf(id) === index);

        // Fetch all report gadgets from Firestore
        const reportGadgets = await Promise.all(
          reportGadgetIds.map((id) =>
            STORE.visualisations
              .getReportGadgetsRef({ accountId: selectedAccountId })
              .doc(id)
              .get()
              .then((doc) => doc.data()),
          ),
        );

        // Filter out any undefined gadgets (deleted gadgets)
        const validReportGadgets = reportGadgets.filter(
          (g): g is ReportGadgets.ReportGadget => g !== undefined,
        );

        // Create mapping of old IDs to new IDs
        const gadgetIdMap = new Map<string, string>();

        // Create and save dashboard gadgets
        const dashboardGadgets: DashboardGadget[] = validReportGadgets.map(
          (rg) => {
            const newId = getIdentifier();
            gadgetIdMap.set(rg.id, newId);

            return buildDashboardGadgetFromSettings({
              id: newId,
              chartDef: rg.chartDef,
              settings: resolveReportGadgetSettings({
                report,
                reportGadget: rg,
              }),
              currentUserId,
              now,
              // The original report is deleted unless kept, so only link to
              // it when it will still exist
              reportDrillDownId: keepOriginal ? report.id : undefined,
            });
          },
        );

        await Promise.all(
          dashboardGadgets.map((dg) =>
            createDashboardGadget(withoutNulls(dg), selectedAccountId),
          ),
        );

        // Transform canvas cards
        const newCards: CanvasCard.Card[] = report.canvas.cards
          .map((card): CanvasCard.Card | undefined => {
            if (card.content.type === 'Report Gadget') {
              const newGadgetId = gadgetIdMap.get(card.content.reportGadgetId);
              if (!newGadgetId) {
                return undefined;
              }
              return {
                layout: card.layout,
                content: {
                  type: 'Dashboard Gadget',
                  dashboardGadgetId: newGadgetId,
                },
              } as CanvasCard.DashboardGadgetCard;
            }
            return card;
          })
          .filter((card): card is CanvasCard.Card => card !== undefined);

        // Create new dashboard
        const newDashboardId = getIdentifier();
        const newDashboard: DashboardType = {
          version: '5',
          id: newDashboardId,
          name: dashboardName,
          canvas: { cards: newCards },
          isTemplate: false,
          variableDrillDowns: [],
          access,
          isV3Published: false,
          v3Access: {
            isPublic: false,
            groupIds: [],
            userIds: [],
          },
          labelIds: [],
          createdBy: currentUserId,
          createdOn: now,
          updatedBy: currentUserId,
          updatedOn: now,
        };

        await STORE.getDashboardsRef({ accountId: selectedAccountId })
          .doc(newDashboard.id)
          .set(withoutNulls(newDashboard));

        // Add dashboard to context before navigating so it's available immediately
        addOrUpdateDashboard(newDashboard);

        // Delete original report if keepOriginal is false
        if (!keepOriginal) {
          await STORE.getReportsRef({ accountId: selectedAccountId })
            .doc(report.id)
            .delete();
        }

        setIsLoading(false);
        onSuccess?.();

        // Navigate to new dashboard
        navigate(
          buildDashboardShow({ id: newDashboardId, portal: selectedPortal }),
        );
        showToast(
          keepOriginal
            ? `Report converted to dashboard "${dashboardName}" (original kept)`
            : `Report converted to dashboard "${dashboardName}"`,
        );
      } catch (error) {
        setIsLoading(false);
        showToast('Error converting report to dashboard');
        console.error('Error converting report to dashboard:', error);
      }
    },
    [
      selectedAccountId,
      currentUserId,
      selectedPortal,
      addOrUpdateDashboard,
      navigate,
      showToast,
    ],
  );

  return {
    convertReportToDashboard,
    isLoading,
  };
};

export default useConvertReportToDashboard;
